import React, { useRef, useState } from 'react';
import { api } from '../api';
import { t } from '../i18n';
import { SectionTitle, Flash } from '../ui';
import Lightbox from './Lightbox';

const MAX_FILE_SIZE = 4 * 1024 * 1024;
const ORDER_ACTIONS = [
    { action: 'first', label: '«' },
    { action: 'up', label: '‹' },
    { action: 'down', label: '›' },
    { action: 'last', label: '»' },
];

/**
 * Gestion des photos d'un lieu : ajout (limite selon la formule premium),
 * réordonnancement et suppression, via l'API JSON.
 */
export default function PhotoManager({ place, onChange }) {
    const fileInput = useRef(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    const images = place.images.filter((image) => image.valide);
    const limit = place.abo ? place.abo.image_limit : 10;
    const canAdd = images.length < limit && !busy;

    const run = async (fn, successKey) => {
        setBusy(true);
        setMessage(null);
        try {
            await fn();
            setMessage({ type: 'success', text: t(successKey) });
            onChange();
        } catch (e) {
            setMessage({ type: 'error', text: t(e.message) });
        } finally {
            setBusy(false);
        }
    };

    const onFileSelected = (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > MAX_FILE_SIZE) {
            setMessage({ type: 'error', text: t('size_error') });
            return;
        }
        run(() => api.addPhoto(place.id, file), 'success_update_image');
    };

    const photoAction = (imageId, action) => {
        if (action === 'delete' && !window.confirm(t('abort') + ' ?')) return;
        run(() => api.updatePhoto(place.id, { image_id: imageId, action }), 'success_update_image');
    };

    return (
        <div id="photos">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <SectionTitle>{t('photos')}</SectionTitle>
                <span className="text-muted text-sm">{images.length} / {limit}</span>
            </div>

            {message && <Flash type={message.type}>{message.text}</Flash>}

            <div className="photo-grid">
                {images.map((image, i) => (
                    <div key={image.id} className="photo-grid__item">
                        <img
                            src={image.url}
                            alt=""
                            style={{ cursor: 'zoom-in' }}
                            onClick={() => setLightbox(i)}
                        />
                        <div style={{
                            position: 'absolute', inset: 'auto 0 0 0', display: 'flex',
                            justifyContent: 'space-between', padding: 6,
                            background: 'linear-gradient(transparent, rgba(15,27,61,0.55))',
                        }}>
                            <span style={{ display: 'flex', gap: 4 }}>
                                {ORDER_ACTIONS.map(({ action, label }) => (
                                    <button
                                        key={action}
                                        className="btn btn--secondary btn--sm"
                                        style={{ padding: '2px 8px' }}
                                        disabled={busy || (['first', 'up'].includes(action) ? i === 0 : i === images.length - 1)}
                                        onClick={() => photoAction(image.id, action)}
                                        title={action}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </span>
                            <button
                                className="btn btn--danger btn--sm"
                                style={{ padding: '2px 8px' }}
                                disabled={busy}
                                onClick={() => photoAction(image.id, 'delete')}
                            >
                                ✕
                            </button>
                        </div>
                    </div>
                ))}

                {canAdd && (
                    <button type="button" className="photo-grid__add" onClick={() => fileInput.current.click()}>
                        <span style={{ fontSize: 24, lineHeight: 1 }}>+</span>
                        {t('add')}
                    </button>
                )}
            </div>

            <input
                ref={fileInput}
                type="file"
                accept="image/png, image/jpeg"
                style={{ display: 'none' }}
                onChange={onFileSelected}
            />

            <Lightbox images={images} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
        </div>
    );
}
